// src/lib/stripe.ts - Update this file

/**
 * Stripe utility functions and configuration
 */

// Get the Stripe publishable key from environment variables or use a default test key
export const STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
  'pk_live_51RF5EWHJklvR4i9iTRsfbXGEM6XZZwFaRxkKOutg2x7oV48JPeVCJJWY6uNL4wzRAVlAzspugdlLDgRobc5SDiX600czfUcSzk'; // Replace with your actual test publishable key

/**
 * Validates if the Stripe publishable key is properly configured
 */
export const isStripeConfigured = (): boolean => {
  return !!STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY.startsWith('pk_');
};

/**
 * Initialize Stripe payment verification for a successful payment
 * @param sessionId The Stripe checkout session ID
 * @param userId The Firebase user ID
 * @returns Promise with verification result
 */
/**
 * Clears any Stripe-related caches from localStorage
 * This helps ensure we don't have stale payment data
 */
export const clearStripeCaches = (): void => {
  try {
    // Find all localStorage keys that might be related to Stripe sessions
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes('verification_lock_') || key.includes('stripe_') || key.includes('checkout_'))
      ) {
        keysToRemove.push(key);
      }
    }

    // Remove all identified keys
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    console.log(`Cleared ${keysToRemove.length} Stripe-related cache items`);
  } catch (error) {
    console.error('Error clearing Stripe caches:', error);
  }
};

/**
 * Creates a direct Stripe checkout session with a hardcoded price of $1.00
 * This bypasses any potential caching issues with the Firebase functions
 * @param userId The Firebase user ID
 * @returns Promise with the checkout URL
 */
export const createDirectCheckoutSession = async (userId: string): Promise<string> => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // Create a direct checkout session with Stripe
    const domain = window.location.origin;

    // Use the Stripe API directly
    const response = await fetch('https://api.stripe.com/v1/checkout/sessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${STRIPE_PUBLISHABLE_KEY}`,
      },
      body: new URLSearchParams({
        success_url: `${domain}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${domain}/forge`,
        mode: 'payment',
        client_reference_id: userId,
        'line_items[0][price_data][currency]': 'usd',
        'line_items[0][price_data][product_data][name]': '200 Actions',
        'line_items[0][price_data][product_data][description]':
          'Purchase 200 actions for VibeCraft',
        'line_items[0][price_data][unit_amount]': '100', // $1.00 in cents
        'line_items[0][quantity]': '1',
        'payment_method_types[]': 'card',
        'metadata[userId]': userId,
        'metadata[actionPackage]': 'standard',
        'metadata[actionsToAdd]': '200',
        'metadata[amount]': '1.0',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create checkout session: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result.url;
  } catch (error) {
    console.error('Error creating direct checkout session:', error);
    throw error;
  }
};

export const verifyStripePayment = async (sessionId: string, userId: string): Promise<any> => {
  if (!sessionId) {
    throw new Error('Session ID is required');
  }

  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const response = await fetch(
      `https://us-central1-vibecraft-5c54c.cloudfunctions.net/verifyStripeSessionHttp`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          userId,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Verification failed: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};
